import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Avatar, Button, InlineError, Skeleton } from '../../components/ui';
import { isRichTextEmpty, sanitizeRichText } from '../../components/RichText';
import {
  createPublicSupabaseClient,
  createSupabaseClient,
  type SupabaseClient,
} from '../../lib/supabase';
import { PanelFold } from '../listView/PanelFold';
import {
  CONSOLE_GROUP_LABELS,
  CONSOLE_GROUP_ORDER,
  PARKED_LABEL,
  TRIP_LABEL,
  consoleViewGroupOf,
  tripTitle,
  type ConsoleViewGroup,
} from './consoleGroup';
import {
  CONSOLE_STATUS_CHOICES,
  CONSOLE_STATUS_LABELS,
  DEFAULT_CONSOLE_GROUP,
  DEFAULT_CONSOLE_STATUS,
  consoleTabCounts,
  consoleView,
  type ConsoleStatusChoice,
} from './consoleView';
import { huddleCommentHtml, huddleCommentTime, newestFirstGroups } from './huddlePresentation';
import {
  HuddleError,
  huddleFileUrl,
  loadHuddle,
  loadConsoleAlerts,
  markConsoleAlertsRead,
  markConsoleTaskRead,
  sendHuddleComment,
  uploadHuddleScreenshot,
  type HuddlePayload,
  type HuddleTask,
  type ConsoleAlert,
  type ConsoleAlerts,
} from './huddleApi';
import { HuddleCommentEditor } from './HuddleCommentEditor';
import { ConsoleFileSheet, type SheetFile } from './ConsoleFileSheet';
import { ConsoleMessageSheet } from './ConsoleMessageSheet';
import { ConsoleAlerts as ConsoleAlertsPanel } from './ConsoleAlerts';
import './huddle.css';
import './linkedinFunnel.css';
import { useConsoleRecorder } from './useConsoleRecorder';
import { ConsoleVoiceRecorder } from './ConsoleVoiceRecorder';
import { sendConsoleVoice } from './huddleApi';

const OFF_MESSAGE = 'This link is turned off. Ask Kyu for a new one.';

/** What Alex sees as the page heading and in the browser tab. */
const PAGE_NAME = 'TAP Console';

// Kyu hid the link on 9 Oct 2026 until he reviews the page; set true to bring it back.
const LINKEDIN_FUNNEL_LINK_VISIBLE = false;

const NO_ALERTS: ConsoleAlerts = { items: [], total: 0, per_task: {}, messages_unread: 0 };

function dayLabel(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function dueLabel(value: string | null): string | null {
  if (value === null) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return `Due ${date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
}

function PublicComment({ body }: { body: string }) {
  const html = useMemo(() => huddleCommentHtml(body), [body]);
  return (
    <div className="huddle-comment-body is-zoomable" dangerouslySetInnerHTML={{ __html: html }} />
  );
}

function TaskCard({
  task,
  token,
  onSent,
  client,
  ownerClient,
  open,
  onToggle,
  unread,
}: {
  task: HuddleTask;
  token: string;
  onSent: () => Promise<void>;
  client: SupabaseClient;
  ownerClient?: SupabaseClient | undefined;
  open: boolean;
  onToggle: () => void;
  unread?: number;
}) {
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sheet, setSheet] = useState<SheetFile | null>(null);
  const closeSheet = useCallback(() => setSheet(null), []);
  const commentEditor = useRef<{ stopDictation: () => void }>(null);
  const due = dueLabel(task.due_date);
  // A "Trip:" title shows without its prefix, and a LasVegas card says only
  // how many files it holds (Kyu, 12 Oct).
  const shownTitle = tripTitle(task.title) ?? task.title;
  const isTrip = shownTitle !== task.title;
  const empty = isRichTextEmpty(body) && !body.includes('<img');
  const detailsId = `huddle-task-details-${task.id}`;
  const commentLabel =
    task.comments.length === 0
      ? 'No comments yet'
      : `${String(task.comments.length)} ${task.comments.length === 1 ? 'comment' : 'comments'}`;
  const countLabel = isTrip
    ? `${String(task.files.length)} ${task.files.length === 1 ? 'file' : 'files'}`
    : task.files.length === 0
      ? commentLabel
      : `${commentLabel} · ${String(task.files.length)} ${task.files.length === 1 ? 'file' : 'files'}`;

  async function submit() {
    commentEditor.current?.stopDictation();
    const html = sanitizeRichText(body).trim();
    if ((isRichTextEmpty(html) && !html.includes('<img')) || html.length > 2000) {
      if (html.length > 2000) setError('That comment is too long. Shorten it and try again.');
      return;
    }
    setError('');
    setSending(true);
    try {
      await sendHuddleComment(client, token, task.id, html, ownerClient);
      setBody('');
      await onSent();
    } catch {
      setError('That comment did not send. Refresh the page and try again.');
    } finally {
      setSending(false);
    }
  }

  return (
    <article className={`huddle-task huddle-task--${task.status}`}>
      <div className="huddle-task-top">
        <span className={`huddle-status huddle-status--${task.status}`}>
          {CONSOLE_STATUS_LABELS[task.status]}
        </span>
        {due === null ? null : <span className="huddle-due">{due}</span>}
        {unread ? (
          <span className="console-new-pill">New{unread > 1 ? ` ${unread}` : ''}</span>
        ) : null}
      </div>
      <h2>
        <button
          type="button"
          className="huddle-task-title"
          aria-expanded={open}
          aria-controls={detailsId}
          onClick={onToggle}
        >
          <span>{shownTitle}</span>
          <span className="huddle-task-chevron" aria-hidden="true">
            {open ? '▴' : '▾'}
          </span>
        </button>
      </h2>
      <p className="huddle-task-summary">{countLabel}</p>

      {open ? (
        <section
          className="huddle-task-details"
          id={detailsId}
          data-image-group
          aria-label={`Comments on ${shownTitle}`}
        >
          <h3>Comments</h3>
          {/* Kyu, 30 Sept: the box sits on top and the newest comment right
              under it, so a sent comment lands where Alex is looking. */}
          {error === '' ? null : <InlineError>{error}</InlineError>}
          <div className="huddle-compose">
            <p className="huddle-posting-as">Posting as Alex</p>
            <HuddleCommentEditor
              ref={commentEditor}
              value={body}
              onChange={setBody}
              disabled={sending || uploading}
              onUpload={async (file) => {
                setError('');
                setUploading(true);
                try {
                  return await uploadHuddleScreenshot(client, token, task.id, file);
                } finally {
                  setUploading(false);
                }
              }}
              onUploadError={() =>
                setError('That screenshot did not upload. Check the file and try again.')
              }
            />
            <Button onClick={() => void submit()} disabled={sending || uploading || empty}>
              {uploading ? 'Uploading…' : sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
          {task.comments.length === 0 ? (
            <p className="huddle-comments-empty">No comments yet.</p>
          ) : (
            <div className="huddle-comments">
              {newestFirstGroups(task.comments).map((group) => (
                <div className="huddle-comment" key={group[0]!.id}>
                  <Avatar
                    name={group[0]!.author}
                    imageUrl={group[0]!.author_avatar_url}
                    size="sm"
                  />
                  <div className="huddle-comment-content">
                    {group.map((comment, index) => (
                      <div className="huddle-comment-entry" key={comment.id}>
                        <p className="huddle-comment-meta">
                          {index === 0 ? <strong>{comment.author}</strong> : null}
                          <time dateTime={comment.created_at}>
                            {huddleCommentTime(comment.created_at)}
                          </time>
                        </p>
                        <PublicComment body={comment.body} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {task.files.length === 0 ? null : (
            <div className="huddle-files is-zoomable" aria-label={`Files on ${shownTitle}`}>
              {task.files.map((file) => {
                const url = huddleFileUrl(token, file.id);
                // No file link opens a new tab (Kyu, 12 Oct). Pictures go to
                // the image viewer (App.tsx mounts it); everything else goes
                // to the in-page file sheet. The href stays as the plain-link
                // fallback if scripts fail.
                return file.mime_type?.startsWith('image/') ? (
                  <a
                    className="huddle-file huddle-file--image"
                    href={url}
                    key={file.id}
                    data-image-viewer
                  >
                    <img src={url} alt={file.name} loading="lazy" />
                    <span>{file.name}</span>
                  </a>
                ) : (
                  <a
                    className="huddle-file"
                    href={url}
                    key={file.id}
                    onClick={(event) => {
                      event.preventDefault();
                      setSheet({ name: file.name, url, mime: file.mime_type ?? null });
                    }}
                  >
                    {file.name}
                  </a>
                );
              })}
            </div>
          )}
        </section>
      ) : null}
      {sheet === null ? null : <ConsoleFileSheet file={sheet} onClose={closeSheet} />}
    </article>
  );
}

export function HuddlePage({
  client: supplied,
  ownerClient: suppliedOwner,
}: { client?: SupabaseClient; ownerClient?: SupabaseClient } = {}) {
  const { token = '' } = useParams<{ token: string }>();
  const [client] = useState(() => supplied ?? createPublicSupabaseClient());
  // The Hub session client, used only to post comments as the signed-in link
  // owner. Tests that pass their own client get no owner client unless they ask.
  const [ownerClient] = useState(
    () => suppliedOwner ?? (supplied ? undefined : createSupabaseClient()),
  );
  const currentToken = useRef(token);
  currentToken.current = token;
  const [payload, setPayload] = useState<HuddlePayload | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [openTaskIds, setOpenTaskIds] = useState<Set<string>>(() => new Set());
  const [alerts, setAlerts] = useState<ConsoleAlerts>(NO_ALERTS);
  const [alertReady, setAlertReady] = useState(false);
  // Design 4e: alerts failing to load hide every count; the tasks still load.
  const live = alertReady ? alerts : NO_ALERTS;
  const [linkOff, setLinkOff] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const recorder = useConsoleRecorder(token);
  const [bellOpen, setBellOpen] = useState(false);
  const [voiceMessageId, setVoiceMessageId] = useState('');
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () =>
      setKeyboardOpen(Boolean(viewport && window.innerHeight - viewport.height > 150));
    viewport?.addEventListener('resize', update);
    return () => viewport?.removeEventListener('resize', update);
  }, []);
  useEffect(() => {
    if (linkOff) recorder.stop(true);
  }, [linkOff, recorder.stop]);
  const [doneOpenNonce, setDoneOpenNonce] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const next = await loadHuddle(client, token);
      setPayload(next);
      setError('');
      setLinkOff(false);
      try {
        setAlerts(await loadConsoleAlerts(client, token));
        setAlertReady(true);
      } catch {
        setAlertReady(false);
      }
    } catch (cause) {
      const off = cause instanceof HuddleError && cause.kind === 'off';
      setLinkOff(off);
      setError(off ? OFF_MESSAGE : 'The huddle could not be loaded. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [client, token]);

  // Opening a card marks it read once (design 4e). The pill and bell count
  // drop on the next 30 s refresh. The call sits outside the state updater:
  // React may run an updater twice.
  const openTask = useCallback(
    (taskId: string) => {
      if (openTaskIds.has(taskId)) return;
      void markConsoleTaskRead(client, token, taskId).catch(() => undefined);
      setOpenTaskIds((current) => new Set(current).add(taskId));
    },
    [client, token, openTaskIds],
  );
  const toggleTask = useCallback(
    (taskId: string) => {
      if (!openTaskIds.has(taskId)) {
        openTask(taskId);
        return;
      }
      setOpenTaskIds((current) => {
        const next = new Set(current);
        next.delete(taskId);
        return next;
      });
    },
    [openTaskIds, openTask],
  );

  useEffect(() => {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const made = meta === null;
    if (meta === null) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.append(meta);
    }
    const previous = meta.content;
    meta.content = 'noindex';
    return () => {
      if (made) meta?.remove();
      else if (meta) meta.content = previous;
    };
  }, []);

  // The tab reads the page name while this page is up, and gives the old
  // title back when it goes, the same give-back as the robots tag above.
  useEffect(() => {
    const previous = document.title;
    document.title = PAGE_NAME;
    return () => {
      document.title = previous;
    };
  }, []);

  // Design 4d [R3]: a SECOND effect, after the give-back one, so the title
  // saved for the give-back is the one from before the page. No cleanup.
  useEffect(() => {
    document.title = live.total ? `(${String(live.total)}) ${PAGE_NAME}` : PAGE_NAME;
  }, [live.total]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  // Kyu, 30 Sept: a tab per group and a status dropdown, both reset to these
  // defaults on every visit (no saved state). consoleView.ts has the rules.
  const [group, setGroup] = useState<ConsoleViewGroup>(DEFAULT_CONSOLE_GROUP);
  const [status, setStatus] = useState<ConsoleStatusChoice>(DEFAULT_CONSOLE_STATUS);
  const tabCounts = consoleTabCounts(payload?.tasks ?? [], status);
  // The LasVegas tab exists only while a trip task is open (Kyu, 12 Oct). If
  // the last one is finished while the tab is showing, fall back to the default.
  const tripShown = tabCounts.trip > 0;
  const activeGroup: ConsoleViewGroup =
    group === 'trip' && !tripShown ? DEFAULT_CONSOLE_GROUP : group;
  const tabKeys: readonly ConsoleViewGroup[] = tripShown
    ? [...CONSOLE_GROUP_ORDER, 'trip']
    : CONSOLE_GROUP_ORDER;
  const view = payload === null ? null : consoleView(payload.tasks, activeGroup, status);
  const doneNew = view?.done.filter((task) => live.per_task[task.id]).length ?? 0;

  // Design 4d: a comment row switches to the task's tab and opens its card.
  // An OPEN task also sets the dropdown to its status; a DONE task leaves the
  // dropdown and opens the Done fold through the nonce (R7, R3-4). Nonce and
  // tab are set here directly, never through an effect on group.
  function openAlert(item: ConsoleAlert) {
    if (item.kind === 'message' || item.task_id === null) {
      setMessagesOpen(true);
      return;
    }
    const task = payload?.tasks.find((candidate) => candidate.id === item.task_id);
    // A parked task (Kyu, 2 Nov) is My Day only, so it has no tab here.
    if (task === undefined || consoleViewGroupOf(task) === 'parked') return;
    setGroup(consoleViewGroupOf(task));
    if (task.status === 'done') setDoneOpenNonce((n) => n + 1);
    else if (consoleViewGroupOf(task) !== 'trip') setStatus(task.status);
    openTask(task.id);
  }

  const card = (task: HuddleTask) => (
    <TaskCard
      task={task}
      token={token}
      onSent={refresh}
      client={client}
      ownerClient={ownerClient}
      open={openTaskIds.has(task.id)}
      onToggle={() => toggleTask(task.id)}
      key={task.id}
      unread={live.per_task[task.id] ?? 0}
    />
  );

  return (
    <main className="huddle-page">
      <div className="huddle-console-scroll" role="region" aria-label="Console list">
        <header className="huddle-header">
          {/* Fixed text, not payload.section_name: the section is named for the
              page, so echoing it here would print the page name twice. */}
          <p className="huddle-eyebrow">4ocean</p>
          <div className="huddle-owner">
            <Avatar
              name={payload?.owner_first_name || 'Kyu'}
              imageUrl={payload?.owner_avatar_url ?? null}
            />
            <h1>{PAGE_NAME}</h1>
            {/* Native, so the phone draws its own picker. No Done choice: Done
                lives only in the fold at the bottom. */}
            <select
              className="ui-select huddle-status-filter"
              aria-label="Show tasks by status"
              value={status}
              onChange={(event) => setStatus(event.target.value as ConsoleStatusChoice)}
            >
              {CONSOLE_STATUS_CHOICES.map((choice) => (
                <option key={choice.key} value={choice.key}>
                  {choice.label}
                </option>
              ))}
            </select>
            {linkOff ? null : (
              <ConsoleAlertsPanel
                open={bellOpen}
                onOpenChange={setBellOpen}
                disabled={recorder.phase === 'recording' || recorder.phase === 'starting'}
                alerts={live}
                ready={alertReady}
                onItem={openAlert}
                onMarkAll={() =>
                  void markConsoleAlertsRead(client, token).then(refresh, () => undefined)
                }
              />
            )}
          </div>
          <p>{dayLabel()}</p>
          {LINKEDIN_FUNNEL_LINK_VISIBLE && !linkOff ? (
            <Link
              className="linkedin-funnel-link"
              to={`/huddle/${encodeURIComponent(token)}/linkedin`}
            >
              LinkedIn funnel
            </Link>
          ) : null}
        </header>

        {linkOff ? null : (
          <button
            className="console-leave-message"
            type="button"
            onClick={() => setMessagesOpen(true)}
          >
            Leave a Message
            {live.messages_unread ? (
              <span>{live.messages_unread === 99 ? '99+' : live.messages_unread}</span>
            ) : null}
          </button>
        )}
        <ConsoleMessageSheet
          open={messagesOpen}
          initialMessageId={voiceMessageId}
          onClose={() => {
            setMessagesOpen(false);
            // The sheet marked the messages read; drop the count now.
            void refresh();
          }}
          client={client}
          token={token}
        />

        {loading && payload === null ? (
          <Skeleton lines={6} label="Loading the huddle" />
        ) : error !== '' ? (
          <InlineError>{error}</InlineError>
        ) : payload === null || view === null ? null : payload.tasks.length === 0 ? (
          <p className="huddle-empty">Nothing is in this huddle yet.</p>
        ) : (
          <>
            <div className="tracker-tab-nav huddle-tabs" role="tablist" aria-label="Task groups">
              {tabKeys.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={activeGroup === key}
                  className={`tracker-tab-button${activeGroup === key ? ' tracker-tab-button--active' : ''}`}
                  onClick={() => {
                    // Design 4d [S2], [R3-4]: the active tab does nothing, and
                    // the nonce resets here, never in an effect on group.
                    if (key !== activeGroup) {
                      setGroup(key);
                      setDoneOpenNonce(0);
                    }
                  }}
                >
                  {/* Kyu, 30 Sept: every tab says its count, 0 too, so an empty
                      Sales/CG never looks like lost work. The shared badge; the
                      space keeps the name "Admin 1" for a screen reader. */}
                  {key === 'trip'
                    ? TRIP_LABEL
                    : key === 'parked'
                      ? PARKED_LABEL
                      : CONSOLE_GROUP_LABELS[key]}{' '}
                  <span className="tracker-tab-badge">{tabCounts[key]}</span>
                  {payload?.tasks.some(
                    (task) => consoleViewGroupOf(task) === key && live.per_task[task.id],
                  ) ? (
                    <span className="console-tab-dot" aria-label="new" role="img" />
                  ) : null}
                </button>
              ))}
            </div>
            {view.open.length === 0 ? (
              <p className="huddle-empty">Nothing here.</p>
            ) : (
              <section className="huddle-task-list" aria-label="Huddle tasks">
                {view.open.map(card)}
              </section>
            )}
            {view.done.length === 0 ? null : (
              // The key resets the fold to closed when the tab changes, and a
              // done-row tap in the bell remounts it open (design 4d, R7).
              <div className="huddle-done" key={`${activeGroup}-${String(doneOpenNonce)}`}>
                <PanelFold
                  title={`Done (${String(view.done.length)})`}
                  summary={doneNew > 0 ? `${String(doneNew)} new` : undefined}
                  defaultOpen={doneOpenNonce > 0}
                >
                  <div className="huddle-task-list">{view.done.map(card)}</div>
                </PanelFold>
              </div>
            )}
          </>
        )}
      </div>
      {!linkOff && payload ? (
        <ConsoleVoiceRecorder
          key={token}
          recorder={recorder}
          hidden={bellOpen || messagesOpen || keyboardOpen}
          onType={() => setMessagesOpen(true)}
          onSend={async (draft) => {
            const id = await sendConsoleVoice(client, token, draft);
            if (currentToken.current !== token) return;
            setVoiceMessageId(id);
            setMessagesOpen(true);
          }}
        />
      ) : null}
    </main>
  );
}
